import { useState, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { AuthShell } from '@/components/AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { HubClientError } from '@verybigthings/trackframe-hub-client';
import { hub } from '@/lib/hub-client';

/** Reads `?token=` off a mailed link; null when absent or empty. */
export function tokenFromSearch(search: string): string | null {
  const token = new URLSearchParams(search).get('token');
  return token && token.length > 0 ? token : null;
}

export function PasswordInput({
  id,
  value,
  onChange,
  revealed,
  onToggle,
  autoFocus,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  revealed: boolean;
  onToggle: () => void;
  autoFocus?: boolean;
}) {
  return (
    <div className="relative">
      <Input
        id={id}
        type={revealed ? 'text' : 'password'}
        autoComplete="new-password"
        required
        minLength={8}
        maxLength={256}
        autoFocus={autoFocus}
        className="pr-10"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={revealed ? 'Hide password' : 'Show password'}
        aria-pressed={revealed}
        className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {revealed ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
      </button>
    </div>
  );
}

/**
 * Public set-password page a mailed link opens (HUB-106 D6): the invite and
 * the self-service reset share ONE token kind and ONE confirm endpoint, so
 * they share this form and differ only in copy. One password input with a
 * reveal toggle — no repeat field. The hub burns the token, revokes any
 * prior session and answers a fresh one (cookies), so success lands the
 * user in the app signed in (HUB-106 D7).
 */
export function SetPasswordForm({
  token,
  title,
  submitLabel,
  expiredMessage,
  expiredAction,
}: {
  token: string | null;
  title: string;
  submitLabel: string;
  expiredMessage: string;
  /** Rendered under the expired message, e.g. a link to request a new mail. */
  expiredAction?: ReactNode;
}) {
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expired = (
    <AuthShell title={title} subtitle="Trackframe">
      <p className="text-sm text-destructive">{expiredMessage}</p>
      {expiredAction}
    </AuthShell>
  );

  if (!token) return expired;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      // Server sets tf_sso + tf_refresh on the response, exactly like login.
      await hub.authPasswordResetConfirm({ body: { token, newPassword: password } });
      window.location.replace('/');
    } catch (err) {
      const invalid = err instanceof HubClientError && err.code === 'token_invalid';
      setError(invalid ? expiredMessage : ((err as Error).message ?? 'Could not set the password'));
      setPending(false);
    }
  };

  return (
    <AuthShell title={title} subtitle="Trackframe">
      <form className="space-y-4" onSubmit={onSubmit}>
        <div className="space-y-2">
          <Label htmlFor="new-password">New password</Label>
          <PasswordInput
            id="new-password"
            value={password}
            onChange={setPassword}
            revealed={revealed}
            onToggle={() => setRevealed((r) => !r)}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">At least 8 characters.</p>
        </div>
        {error && (
          <div className="space-y-2">
            <p className="text-sm text-destructive">{error}</p>
            {error === expiredMessage && expiredAction}
          </div>
        )}
        <Button type="submit" className="w-full" disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </Button>
      </form>
    </AuthShell>
  );
}
